import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Bell, Lock, Palette, Globe, Building2, Cpu, Paintbrush, Plus, Trash2, Check, Rocket } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [orgSettings, setOrgSettings] = useState({});
  const [saving, setSaving] = useState(false);
  
  // BrandKit state
  const [brandkits, setBrandkits] = useState([]);
  const [newBrandkit, setNewBrandkit] = useState({
    name: '',
    primary_color: '#3A7BFF',
    secondary_color: '#0F1A2E',
    accent_color: '#39E6C8'
  });

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'organization', label: 'Organization', icon: Building2 },
    { id: 'autopilot', label: 'Autopilot', icon: Rocket },
    { id: 'brandkit', label: 'Brand Kit', icon: Paintbrush },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'integrations', label: 'Integrations', icon: Globe },
  ];

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrg) {
      loadOrgSettings();
      fetchBrandkits();
    }
  }, [selectedOrg]);

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${API}/organizations`, { withCredentials: true });
      setOrganizations(response.data);
      if (response.data.length > 0) {
        setSelectedOrg(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const loadOrgSettings = () => {
    const org = organizations.find(o => o.id === selectedOrg);
    if (org) {
      setOrgSettings(org.settings || {});
    }
  };

  const fetchBrandkits = async () => {
    try {
      const response = await axios.get(`${API}/brandkits/${selectedOrg}`, { withCredentials: true });
      setBrandkits(response.data);
    } catch (error) {
      console.error('Failed to fetch brandkits:', error);
    }
  };

  const handleCreateBrandkit = async () => {
    if (!newBrandkit.name) {
      toast.error('Please enter a name');
      return;
    }

    try {
      await axios.post(
        `${API}/brandkits`,
        {
          organization_id: selectedOrg,
          ...newBrandkit
        },
        { withCredentials: true }
      );
      toast.success('BrandKit created');
      setNewBrandkit({ name: '', primary_color: '#3A7BFF', secondary_color: '#0F1A2E', accent_color: '#39E6C8' });
      fetchBrandkits();
    } catch (error) {
      toast.error('Failed to create BrandKit');
    }
  };

  const handleSetDefault = async (brandkitId) => {
    try {
      await axios.put(
        `${API}/brandkits/${brandkitId}`,
        { is_default: true },
        { withCredentials: true }
      );
      toast.success('Default BrandKit updated');
      fetchBrandkits();
    } catch (error) {
      toast.error('Failed to update BrandKit');
    }
  };

  const handleDeleteBrandkit = async (brandkitId) => {
    try {
      await axios.delete(`${API}/brandkits/${brandkitId}`, { withCredentials: true });
      toast.success('BrandKit deleted');
      fetchBrandkits();
    } catch (error) {
      toast.error('Failed to delete BrandKit');
    }
  };

  const handleSettingChange = async (key, value) => {
    const newSettings = { ...orgSettings, [key]: value };
    setOrgSettings(newSettings);
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/organizations/${selectedOrg}/settings`,
        { [key]: value },
        { withCredentials: true }
      );
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
      console.error('Failed to update settings:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 space-y-8" data-testid="settings-page">
      <div>
        <h1 className="text-4xl font-bold text-white mb-2">Settings</h1>
        <p className="text-[#8A93A6] text-lg">Manage your account and preferences.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Tabs */}
        <div className="glass rounded-2xl p-4 space-y-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`settings-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#3A7BFF] text-white'
                    : 'text-[#8A93A6] hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon size={20} />
                <span className="font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="lg:col-span-3 glass rounded-2xl p-6">
          <h2 className="text-2xl font-bold text-white mb-6">
            {tabs.find((t) => t.id === activeTab)?.label}
          </h2>
          
          {activeTab === 'organization' ? (
            <div className="space-y-6">
              {/* Organization Selector */}
              <div>
                <label className="block text-sm font-medium text-[#8A93A6] mb-2">Select Organization</label>
                <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                  <SelectTrigger className="w-full max-w-md">
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Processing Engine Toggle */}
              <div className="border-t border-white/10 pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Cpu size={20} className="text-[#3A7BFF]" />
                      <h3 className="text-lg font-bold text-white">Processing Engine</h3>
                    </div>
                    <p className="text-sm text-[#8A93A6] max-w-xl">
                      Enable automatic processing of uploaded videos. When enabled, uploads will be cleaned and prepared for clipping.
                    </p>
                  </div>
                  <Switch
                    checked={orgSettings.processing_enabled !== false}
                    onCheckedChange={(checked) => handleSettingChange('processing_enabled', checked)}
                    disabled={saving}
                    data-testid="processing-enabled-toggle"
                  />
                </div>
              </div>

              {/* Clip Engine Toggle */}
              <div className="border-t border-white/10 pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Paintbrush size={20} className="text-[#39E6C8]" />
                      <h3 className="text-lg font-bold text-white">Clip Engine</h3>
                    </div>
                    <p className="text-sm text-[#8A93A6] max-w-xl">
                      Enable clip creation and merging features. When disabled, the Clips page will be hidden.
                    </p>
                  </div>
                  <Switch
                    checked={orgSettings.clip_engine_enabled !== false}
                    onCheckedChange={(checked) => handleSettingChange('clip_engine_enabled', checked)}
                    disabled={saving}
                  />
                </div>
              </div>

              {/* Branding Toggle */}
              <div className="border-t border-white/10 pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-2">Branding</h3>
                    <p className="text-sm text-[#8A93A6] max-w-xl">
                      Enable BrandKit application to clips. When disabled, clips will be created without overlays.
                    </p>
                  </div>
                  <Switch
                    checked={orgSettings.branding_enabled !== false}
                    onCheckedChange={(checked) => handleSettingChange('branding_enabled', checked)}
                    disabled={saving}
                  />
                </div>
              </div>

              {/* Optimizer Toggle */}
              <div className="border-t border-white/10 pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Cpu size={20} className="text-[#39E6C8]" />
                      <h3 className="text-lg font-bold text-white">Optimizer Engine</h3>
                    </div>
                    <p className="text-sm text-[#8A93A6] max-w-xl">
                      Enable platform optimization and variant generation. When disabled, the Optimizer page will be hidden.
                    </p>
                  </div>
                  <Switch
                    checked={orgSettings.optimizer_enabled !== false}
                    onCheckedChange={(checked) => handleSettingChange('optimizer_enabled', checked)}
                    disabled={saving}
                  />
                </div>
              </div>

              {/* Dropbox Integration */}
              <div className="border-t border-white/10 pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-2">Dropbox Storage</h3>
                    <p className="text-sm text-[#8A93A6] max-w-xl">
                      Connect your Dropbox account to automatically save all content with organized folder structure.
                    </p>
                  </div>
                  <a href="/settings/storage" className="btn-primary">
                    Manage Storage
                  </a>
                </div>
              </div>
            </div>
          ) : activeTab === 'autopilot' ? (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border border-orange-500/20 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <Rocket className="h-8 w-8 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Autopilot Automation</h3>
                    <p className="text-[#8A93A6] mb-4">
                      Automate your content workflow with AI-powered autopilot. Process lives, generate clips, and schedule posts automatically.
                    </p>
                    <button
                      onClick={() => window.location.href = '/settings/autopilot'}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                    >
                      Configure Autopilot →
                    </button>
                  </div>
                </div>
              </div>

              <div className="border-t border-white/10 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">Features</h3>
                <div className="space-y-3 text-[#8A93A6]">
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-white">Live Autopilot</div>
                      <div className="text-sm">Automatically process and save live stream recordings</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-white">Clip Autopilot</div>
                      <div className="text-sm">Generate clips from long-form content using AI</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-white">Scheduler Autopilot</div>
                      <div className="text-sm">Automatically schedule and post content to platforms</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-white">Community Autopilot</div>
                      <div className="text-sm">Auto-reply to comments (Enterprise plan)</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : activeTab === 'brandkit' ? (
            <div className="space-y-6">
              {/* Organization Selector */}
              <div>
                <label className="block text-sm font-medium text-[#8A93A6] mb-2">Select Organization</label>
                <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                  <SelectTrigger className="w-full max-w-md">
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Create New BrandKit */}
              <div className="border-t border-white/10 pt-6">
                <h3 className="text-lg font-bold text-white mb-4">Create New BrandKit</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    type="text"
                    placeholder="BrandKit Name"
                    value={newBrandkit.name}
                    onChange={(e) => setNewBrandkit({...newBrandkit, name: e.target.value})}
                  />
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="text-xs text-[#8A93A6]">Primary Color</label>
                      <input
                        type="color"
                        value={newBrandkit.primary_color}
                        onChange={(e) => setNewBrandkit({...newBrandkit, primary_color: e.target.value})}
                        className="w-full h-10 rounded cursor-pointer"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-[#8A93A6]">Secondary</label>
                      <input
                        type="color"
                        value={newBrandkit.secondary_color}
                        onChange={(e) => setNewBrandkit({...newBrandkit, secondary_color: e.target.value})}
                        className="w-full h-10 rounded cursor-pointer"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-[#8A93A6]">Accent</label>
                      <input
                        type="color"
                        value={newBrandkit.accent_color}
                        onChange={(e) => setNewBrandkit({...newBrandkit, accent_color: e.target.value})}
                        className="w-full h-10 rounded cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleCreateBrandkit}
                  className="mt-4 btn-primary flex items-center gap-2"
                  data-testid="create-brandkit-button"
                >
                  <Plus size={18} />
                  Create BrandKit
                </button>
              </div>

              {/* BrandKit List */}
              <div className="border-t border-white/10 pt-6">
                <h3 className="text-lg font-bold text-white mb-4">Existing BrandKits</h3>
                {brandkits.length === 0 ? (
                  <p className="text-[#8A93A6]">No BrandKits yet. Create your first one above.</p>
                ) : (
                  <div className="space-y-3">
                    {brandkits.map((bk) => (
                      <div
                        key={bk.id}
                        className="p-4 rounded-xl border border-white/10 hover:border-white/20"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="flex gap-1">
                              <div
                                className="w-6 h-6 rounded"
                                style={{ backgroundColor: bk.primary_color }}
                              />
                              <div
                                className="w-6 h-6 rounded"
                                style={{ backgroundColor: bk.secondary_color }}
                              />
                              <div
                                className="w-6 h-6 rounded"
                                style={{ backgroundColor: bk.accent_color }}
                              />
                            </div>
                            <div>
                              <h4 className="text-white font-medium">{bk.name}</h4>
                              {bk.is_default && (
                                <span className="text-xs text-[#39E6C8]">Default</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {!bk.is_default && (
                              <button
                                onClick={() => handleSetDefault(bk.id)}
                                className="btn-secondary text-sm flex items-center gap-1"
                              >
                                <Check size={14} />
                                Set Default
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteBrandkit(bk.id)}
                              className="p-2 rounded-lg hover:bg-[#FF4F4F]/10 text-[#FF4F4F]"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-96 flex items-center justify-center border-2 border-dashed border-white/10 rounded-xl">
              <p className="text-[#8A93A6]">{tabs.find((t) => t.id === activeTab)?.label} settings will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
